import { useMemo } from 'react';
import {
  GetPatientsBalanceResponse,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useGetPatientBalancesSummaryQuery } from '@frontend/api-collections';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert, ModalControlTriggerProps } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../../store/bulk-collections-filter-store';
import { useBulkCollectionSelectAllShallowStore } from '../../../../store/bulk-collections-select-all-store';
import { BulkCreateStepId } from '../../BulkCreateModal/bulk-create-modal';
import { Row } from '../../common/mobile-list/row';
import { RowSelect } from '../../common/row-select';
import { getFilterString, searchByText } from '../../utils';
import { MobileToolBar } from '../top-toolbar/mobile-toolbar';
import { useBulkCollectionsInfiniteScroll } from './hooks/use-bulk-collections-infinite-scroll';

type MobileListProps = {
  isLoading: boolean;
  collections?: GetPatientsBalanceResponse;
  selected: string[];
  toggleSelection: (patientId: string) => void;
  useNewApi: boolean;
  triggerProps: ModalControlTriggerProps;
  toggleInitialStep: (stepId: BulkCreateStepId) => void;
};
export const MobileList = ({
  isLoading,
  collections,
  selected,
  toggleSelection,
  useNewApi,
  triggerProps,
  toggleInitialStep,
}: MobileListProps) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const alert = useAlert();
  const { balanceType, searchValue } = useBulkCollectionsFilterShallowStore('balanceType', 'searchValue');
  const { isAllSelected, reset, selectedCount, setIsAllSelected, setFilters, excluded } =
    useBulkCollectionSelectAllShallowStore(
      'isAllSelected',
      'reset',
      'selectedCount',
      'setIsAllSelected',
      'setFilters',
      'excluded'
    );

  // New API flow - infinite scroll
  const {
    data: infiniteScrollData,
    isLoading: isInfiniteScrollLoading,
    isFetchingNextPage,
    hasNextPage,
    loadMore,
  } = useBulkCollectionsInfiniteScroll({
    enabled: useNewApi ?? false,
  });

  const { data: summary, isLoading: isTotalCountLoading } = useGetPatientBalancesSummaryQuery({
    request: {
      locationIds: selectedLocationIds,
      filter: getFilterString(balanceType, searchValue),
    },
    options: {
      enabled: useNewApi && selectedLocationIds.length > 0,
      refetchOnMount: 'always',
    },
  });

  // Legacy client-side filtering
  const filteredCollections = useMemo(() => {
    if (useNewApi) return [];

    let result = collections?.patientBalances ?? [];
    if (searchValue) {
      result = searchByText(result, searchValue);
    }

    if (balanceType.length > 0) {
      result = result.filter((item) => balanceType.includes(item.balanceType));
    }

    return result;
  }, [useNewApi, balanceType, searchValue, collections?.patientBalances]);

  const listData = useNewApi ? infiniteScrollData : filteredCollections;
  const loading = useNewApi ? isInfiniteScrollLoading : isLoading;
  const hasNext = useNewApi ? hasNextPage : false;
  const loadMoreFn = useNewApi ? loadMore : undefined;
  const isFetchingMore = useNewApi ? isFetchingNextPage : false;

  const handleSelectAll = (val: boolean) => {
    setIsAllSelected(val, summary?.totalBalancesWithPhoneNumbers ?? 0);
    if (val) {
      setFilters({
        ...(balanceType?.length && { balanceType }),
        ...(searchValue && { searchValue }),
      });
    } else {
      reset();
    }
  };

  return (
    <>
      {useNewApi && (
        <MobileToolBar
          count={selectedCount}
          onClear={() => {
            handleSelectAll(false);
          }}
          onSelectAll={() => {
            handleSelectAll(true);
          }}
          selectionState={
            isAllSelected && excluded.length === 0 && selectedCount === summary?.totalBalancesWithPhoneNumbers
              ? 'all'
              : selectedCount > 0
              ? 'some'
              : 'none'
          }
          isSelectionDisabled={
            isTotalCountLoading || (summary?.totalBalancesWithPhoneNumbers === 0 && selectedCount === 0)
          }
          triggerProps={triggerProps}
          toggleInitialStep={toggleInitialStep}
        />
      )}
      <PaginatedList
        listData={listData}
        renderListItem={({ index, listItem }) => (
          <Row
            index={index}
            selected={selected}
            data={listItem}
            onClick={(data: GetPatientsBalanceResponse_PatientBalance) => {
              if (!data.phoneNumber) {
                alert.error(t("Request can't be created because no phone number is on file"));
              } else {
                toggleSelection(data.patientId);
              }
            }}
            CustomSelectCheckbox={useNewApi ? () => <RowSelect balance={listItem} /> : undefined}
          />
        )}
        hasNextPage={hasNext}
        loadMore={loadMoreFn}
        isLoading={loading}
        isFetchingNextPage={isFetchingMore}
        endOfListText={`🎉 ${t('You have reached the end')}`}
      />
    </>
  );
};
